import React, { ReactElement, useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FONT_STYLE } from '@bucketplace/design-system';

import { isIOS } from '../../../utils';
import { BadgeType, TextBadge as TextBadgeType } from './types';

interface TextBadgeList {
  circle: TextBadgeType[];
  banner: TextBadgeType[];
  small: TextBadgeType[];
}

type ContentData = Pick<TextBadgeType, 'color' | 'position'>;

const SEGMENTS = 3;

const calculateCol = (
  elementSize: number,
  position: number,
  spacing?: number,
): string => {
  const halfSize = elementSize / 2;
  const spacingSize = spacing ? halfSize - spacing : 0;
  switch (Math.ceil(position / SEGMENTS)) {
    case 1:
      return `calc(0% - ${halfSize}px + ${spacingSize}px)`;
    case 2:
      return `calc(50% - ${halfSize}px)`;
    case 3:
    default:
      return `calc(100% - ${halfSize}px - ${spacingSize}px)`;
  }
};

const calculateRow = (
  elementSize: number,
  position: number,
  spacing?: number,
): string => {
  const halfSize = elementSize / 2;
  const spacingSize = spacing ? halfSize - spacing : 0;
  switch (position % SEGMENTS) {
    case 1:
      return `calc(0% - ${halfSize}px + ${spacingSize}px)`;
    case 2:
      return `calc(50% - ${halfSize}px)`;
    case 0:
    default:
      return `calc(100% - ${halfSize}px - ${spacingSize}px)`;
  }
};

export interface TextBadgeProps {
  badgeType: 'none' | BadgeType;
  textBadgeList: TextBadgeType[];
  flex?: boolean;
}

export function TextBadge({
  badgeType,
  textBadgeList,
  flex,
}: TextBadgeProps): ReactElement {
  const isWindow = !isIOS();

  const filteredBadgeList = useMemo(() => {
    return textBadgeList
      .map((badge) => ({
        ...badge,
        position: badge.position ? Math.max(1, Math.min(badge.position, 9)) : undefined,
      }))
      .filter((item) => item.type === badgeType)
      .filter((item) => !!item.value)
      .reduce((accBadgeList, badge) => {
        const key = badge.type;
        if (!accBadgeList[key]) {
          return accBadgeList;
        }
        accBadgeList[key].push(badge);
        return accBadgeList;
      }, {
        circle: [],
        banner: [],
        small: [],
      } as TextBadgeList);
  }, [badgeType, textBadgeList]);

  const { circle, banner, small } = filteredBadgeList;

  return (
    <>
      { small.length > 0 && (
        <SmallWrapper>
          <div>
            { small.map(({ value, color }, index) => (
              <SmallContent key={`${index + 1}`} color={color}>
                <span>{ value }</span>
              </SmallContent>
            )) }
          </div>
        </SmallWrapper>
      ) }
      { circle.length > 0 && (
        <CircleContainer>
          { circle.map(({ value, color, position }, index) => (
            <CircleContent
              key={`${index + 1}`}
              color={color}
              position={position}
              isWindow={isWindow}
            >
              <span>{ value }</span>
            </CircleContent>
          )) }
        </CircleContainer>
      ) }
      { banner.length > 0 && (
        <BannerContainer>
          { banner.map(({ value, color }, index) => (
            <BannerContent
              key={`${index + 1}`}
              color={color}
            >
              <span>{ value }</span>
            </BannerContent>
          )) }
        </BannerContainer>
      ) }
    </>
  );
}

const SpoqaHanSansNeoFont = css`
  @font-face {
    font-family: 'Spoqa Han Sans Neo';
    src: url('https://asset.ohousecdn.com/assets/v1/161295166983234262.woff') format('woff');
    font-weight: bold;
    font-style: normal;
  }
  font-family: "Spoqa Han Sans Neo", "Noto Sans KR", "Apple SD Gothic Neo", "맑은 고딕", "Malgun Gothic", sans-serif;
`;

const containerStyle = css`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  z-index: 3;
  pointer-events: none;
  & + img {
    border-radius: 4px;
  }
`;

const textOverflowStyle = css`
  display: flex;
  > span {
    display: inline-block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SmallWrapper = styled.div`
  ${containerStyle}
  position: absolute;
  display: flex;
  align-items: end;
  padding: 8px;
`;

const SmallContent = styled.div<ContentData>`
  ${FONT_STYLE.caption2}
  ${SpoqaHanSansNeoFont}
  ${textOverflowStyle}
  display: inline-flex;
  padding: 3px 6px;
  background-color: ${({ color }) => color};
  color: ${({ theme }) => theme.content.base6};
  font-weight: 700;
  border-radius: 2px;
  &:not(:last-of-type){
    margin-right: 4px;
  }
`;

const CircleContainer = styled.div`
  ${containerStyle}
  position: absolute;
`;

const CircleContent = styled.div<ContentData & { isWindow: boolean }>`
  ${FONT_STYLE.caption1}
  ${textOverflowStyle}
  ${SpoqaHanSansNeoFont}
  ${({ position }) => css`
    top: ${calculateCol(54, position ?? 1, 10)};
    left: ${calculateRow(54, position ?? 1, 6)};
  `};
  ${({ isWindow }) => isWindow && css`
    font-size: 11px;
  `};
  width: 56px;
  height: 56px;
  position: absolute;
  display: flex;
  align-items: center;
  vertical-align: bottom;
  background-color: ${({ color }) => color};
  color: ${({ theme }) => theme.content.base6};
  text-align: center;
  font-weight: 700;
  border-radius: 50%;
  > span {
    word-break: keep-all;
  }
`;

const BannerContainer = styled.div`
  ${containerStyle}
  position: absolute;
`;

const BannerContent = styled.div<ContentData>`
  ${FONT_STYLE.caption1}
  ${textOverflowStyle}
  ${SpoqaHanSansNeoFont}
  width: 100%;
  position: absolute;
  top: -10px;
  padding: 7px 8px;
  background-color: ${({ color }) => color};
  color: ${({ theme }) => theme.content.base6};
  text-align: center;
  font-weight: 700;
  border-radius: 6px 6px 0px 0px;
  > span {
    word-break: keep-all;
  }
`;
